// Digest archive data layer (#328). Reads the list (`GET /v1/digests`) and the
// per-digest reading view (`GET /v1/digests/:id`, which carries the stored email
// HTML). Mirrors lib/repos: round-trips over `apiFetch` reusing the session
// bearer, one `createQuery` per view.

import type { DigestWire } from '@developerz/domain';
import { DigestDetailWireSchema, DigestWireSchema } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch, withCursor } from './api';
import type { Page } from './paged';
import { pageSchema } from './paged-schema';
import { SESSION_TOKEN } from './session';

/**
 * `/v1/digests` list row — INFERRED from the one wire declaration
 * (`DigestResource`, `packages/domain/src/wire/digest.ts`) that both this route
 * and the MCP `digest` verb serialize through, so a header field added there
 * arrives here as a type error rather than as a silently missing column.
 * `Readonly` because this app treats a response as immutable.
 */
export type DigestSummary = Readonly<DigestWire>;

/**
 * `/v1/digests/:id` — the summary plus the stored MJML→HTML the cron rendered.
 *
 * The intersection mirrors the route's own composition: `rendered_html` is
 * deliberately NOT part of `DigestResource` (the archive list omits the stored
 * HTML), and `projectDigestDetail` (`apps/api/src/routes/digests.ts`) spreads
 * the resource and adds the field, exactly as this type does.
 */
export type DigestDetail = DigestSummary & { readonly rendered_html: string };

/**
 * Both reads PARSED (plan 101 slice 15 step 3), and through the two resources
 * the route itself composes: `DigestDetailWireSchema` IS
 * `DigestWireSchema.extend({ rendered_html })`, the same composition
 * `projectDigestDetail` performs server-side. So the archive card and the
 * reading pane cannot disagree about a header field, and a drifted one lands as
 * this drawer's own error state rather than as blank prose.
 */
const digestsPageSchema = pageSchema(DigestWireSchema);

/** Delivery lifecycle stage, furthest-reached first. Drives the card status pill. */
export type DigestStatus = 'opened' | 'delivered' | 'sent' | 'queued';

/** The furthest stage a digest reached, derived from its timestamps. */
export function digestStatus(d: DigestSummary): DigestStatus {
  if (d.opened_at) return 'opened';
  if (d.delivered_at) return 'delivered';
  if (d.sent_at) return 'sent';
  return 'queued';
}

export const DIGESTS_QUERY_KEY = 'digests';

/**
 * One page of the account's past digests, newest-first. Idle (no fetch) when
 * there's no session. The route is keyset-paginated over `period_start` with an
 * opaque cursor (`apps/api/src/routes/digests.ts`), so an archive older than a
 * page is reached through `usePagedQuery` + <LoadMore/>, never dropped.
 */
export function useDigests(cursor: () => string | null, token: string | null = SESSION_TOKEN) {
  return createQuery<Page<DigestSummary>>(() => ({
    queryKey: [DIGESTS_QUERY_KEY, cursor(), token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => {
      const at = cursor();
      const path = withCursor('/v1/digests', at);
      return apiFetch(path, token, undefined, { schema: digestsPageSchema });
    },
  }));
}

/**
 * One digest's reading view (with `rendered_html`). Idle until `id()` is set —
 * the drawer only fetches the body once a card is opened.
 */
export function useDigest(id: () => string | null, token: string | null = SESSION_TOKEN) {
  return createQuery<DigestDetail>(() => ({
    queryKey: [DIGESTS_QUERY_KEY, 'detail', id(), token, activeAccountId()],
    enabled: id() !== null && token !== null,
    queryFn: () =>
      apiFetch(`/v1/digests/${id()}`, token, undefined, { schema: DigestDetailWireSchema }),
  }));
}
